// src/hr/kit/Feedback.js -- empty states and loading skeletons.
import React from 'react';
import { FiInbox } from 'react-icons/fi';
import { cx, renderIcon } from './util';

// A calm, centered message: glowing icon orb, title, short body, optional action.
// compact: tighter version for use inside a card.
export function EmptyState({ icon = FiInbox, title, children, action, compact = false, className }) {
  return (
    <div className={cx('hrp-empty', compact && 'hrp-empty--compact', className)}>
      <div className="hrp-empty__orb" aria-hidden="true">{renderIcon(icon)}</div>
      {title && <h2 className="hrp-empty__title">{title}</h2>}
      {children && <div className="hrp-empty__body">{children}</div>}
      {action && <div className="hrp-empty__act">{action}</div>}
    </div>
  );
}

const px = (v) => (typeof v === 'number' ? `${v}px` : v);

// variant: 'line' (default) | 'circle' | 'block' | 'card' (a glass card with a ring and lines).
// lines: renders that many text lines, the last one shorter. label: announced once to screen
// readers as a polite status ("Loading your time off"); without it the skeleton is hidden from them.
export function Skeleton({ variant = 'line', lines, width, height, radius, label, className }) {
  let shape;
  if (variant === 'card') {
    shape = (
      <div className="hrp-skel-card">
        <span className="hrp-skel hrp-skel--circle" style={{ width: 72, height: 72 }} />
        <div className="hrp-skel-card__lines">
          <span className="hrp-skel" style={{ width: '38%', height: 10 }} />
          <span className="hrp-skel" style={{ width: '62%', height: 22 }} />
          <span className="hrp-skel" style={{ width: '84%', height: 10 }} />
        </div>
      </div>
    );
  } else if (lines) {
    shape = (
      <div className="hrp-skel-lines">
        {Array.from({ length: lines }, (_, i) => (
          <span
            key={i}
            className="hrp-skel"
            style={{ width: i === lines - 1 && lines > 1 ? '62%' : px(width) || '100%', height: px(height) || 10 }}
          />
        ))}
      </div>
    );
  } else {
    const circle = variant === 'circle';
    shape = (
      <span
        className={cx('hrp-skel', circle && 'hrp-skel--circle', variant === 'block' && 'hrp-skel--block')}
        style={{
          width: px(width) || (circle ? '40px' : '100%'),
          height: px(height) || (circle ? px(width) || '40px' : variant === 'block' ? '96px' : '10px'),
          borderRadius: px(radius)
        }}
      />
    );
  }

  if (!label) return <div className={cx('hrp-skel-wrap', className)} aria-hidden="true">{shape}</div>;
  return (
    <div className={cx('hrp-skel-wrap', className)} role="status" aria-live="polite">
      <span className="hrp-sr">{label}</span>
      <div aria-hidden="true">{shape}</div>
    </div>
  );
}
